import { Box, Typography, IconButton, Tooltip } from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import { formatMarkdownToSafeHtml } from '../../../../utils/html';

const MensagemCard = ({
  item,
  dragHandleProps,
  onEdit,
  onDelete,
  flowInfo
}) => {
  const fontFamily = '"Segoe UI", "Helvetica Neue", sans-serif';
  const pausesFlow = Boolean(item?.pergunta || item?.pergunta_id);
  const { sendLabel, sendTooltip } = flowInfo || {};
  const safeSendLabel = sendLabel || 'sequência do fluxo';
  const atributoLabel = item?.pergunta?.atributo
    ? `${item.pergunta.atributo.charAt(0).toUpperCase()}${item.pergunta.atributo.slice(1)}`
    : null;
  const responseAttribute = atributoLabel ? `Atributo coletado: ${atributoLabel}` : null;

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        maxWidth: '600px',
        fontFamily,
      }}
    >
      {/* Caixa da mensagem do bot */}
      <Box
        sx={{
          backgroundColor: '#fff',
          borderRadius: '16px',
          padding: 2,
          boxShadow: '0 1px 5px rgba(0,0,0,0.1)',
          transition: 'all 0.25s ease',
          transform: 'scale(1)',
          '&:hover': {
            boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
            transform: 'scale(1.015)',
            backgroundColor: '#fdfdfd',
          },
          fontFamily,
          position: 'relative',
          pr: 6,
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 16,
            left: -8,
            width: 0,
            height: 0,
            borderTop: '8px solid transparent',
            borderBottom: '8px solid transparent',
            borderRight: '8px solid #fff',
          },
        }}
      >
        {/* Título */}
        <Box display="flex" alignItems="center" gap={1} mb={1.5}>
          <Typography
            sx={{
              fontWeight: 'bold',
              fontSize: '1rem',
              color: '#000',
              borderBottom: '2px solid #d30038',
              display: 'inline-block',
              fontFamily,
            }}
          >
            Mensagem {item.ordem}:
          </Typography>
          {item.pergunta?.atributo && (
            <Box
              component="span"
              sx={{
                bgcolor: '#fff0f3',
                color: '#d30038',
                px: 1.5,
                py: 0.2,
                fontWeight: 'bold',
                borderRadius: '20px',
                fontSize: '0.8rem',
                border: '1px solid #d30038',
                fontFamily,
              }}
            >
              {atributoLabel}
            </Box>
          )}
        </Box>
        <Box
          sx={{
            borderRadius: 1.5,
            border: '1px solid #ededed',
            bgcolor: '#fafafa',
            px: 1.5,
            py: 1,
            mb: 1.5,
            fontFamily,
          }}
        >
          {sendTooltip ? (
            <Tooltip title={sendTooltip}>
              <Typography
                variant="caption"
                sx={{ color: '#4b5563', fontFamily, cursor: 'help' }}
              >
                Envio: {safeSendLabel}
              </Typography>
            </Tooltip>
          ) : (
            <Typography variant="caption" sx={{ color: '#4b5563', fontFamily }}>
              Envio: {safeSendLabel}
            </Typography>
          )}
        </Box>

        {/* Conteúdo da mensagem */}
        <Typography
          variant="body1"
          sx={{
            whiteSpace: 'pre-line',
            fontSize: '1rem',
            color: '#000',
            fontFamily,
          }}
          dangerouslySetInnerHTML={{
            __html: formatMarkdownToSafeHtml(item.mensagem || '', { style: 'whatsapp' }),
          }}
        />

        {/* Botões flutuantes */}
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            display: 'flex',
            gap: 0.5,
          }}
        >
          <Tooltip title="Editar">
            <IconButton onClick={onEdit} size="small" aria-label="Editar mensagem">
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Excluir">
            <IconButton onClick={onDelete} size="small" aria-label="Excluir mensagem">
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {pausesFlow && (
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            alignItems: 'flex-start',
            borderRadius: 1.5,
            border: '1px solid #f2d3a7',
            bgcolor: '#fff7ec',
            px: 1.5,
            py: 1,
            my: 1.5,
            width: '100%',
            fontFamily,
          }}
        >
          <PauseCircleOutlineIcon sx={{ color: '#a16207', mt: '2px' }} fontSize="small" />
          <Box>
            <Typography
              variant="caption"
              sx={{ fontWeight: 700, color: '#8a1d3f', display: 'block', fontFamily }}
            >
              Fluxo pausa aqui
            </Typography>
            <Typography variant="caption" sx={{ color: '#6b7280', fontFamily }}>
              O sistema aguarda a resposta do cliente para enviar a próxima mensagem.
            </Typography>
          </Box>
        </Box>
      )}

      {/* Resposta do cliente (nó do fluxo) */}
      {item.pergunta && (
        <Box
          sx={{
            mt: 2,
            mx: 'auto',
            bgcolor: '#f0fdf4',
            px: 2,
            py: 1.5,
            borderRadius: 2,
            color: '#0f172a',
            fontFamily,
            width: '100%',
            maxWidth: '520px',
            border: '1px dashed #9ae6b4',
          }}
        >
          <Typography variant="caption" sx={{ color: '#4b5563', display: 'block', mb: 0.75, fontFamily }}>
            O fluxo continua somente após esta resposta.
          </Typography>
          {responseAttribute && (
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#14532d', fontFamily }}>
              {responseAttribute}
            </Typography>
          )}
        </Box>
      )}

      {/* Ícone de arrastar - fora e à direita, centralizado verticalmente */}
      <Box
        {...dragHandleProps}
        sx={{
          position: 'absolute',
          right: -40,
          top: '50%',
          transform: 'translateY(-50%)',
          cursor: 'grab',
        }}
      >
        <Tooltip title="Arrastar">
          <Box
            sx={{
              bgcolor: '#f0f0f0',
              borderRadius: '8px',
              p: 0.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <DragIndicatorIcon sx={{ color: '#999' }} />
          </Box>
        </Tooltip>
      </Box>
    </Box>
  );
};

export default MensagemCard;
